import { AssignmentStatusType, AssignmentType } from '../type';

export type AssignmentItem = {
  id: number;
  book: string;
  lesson: string;
  code: string;
  title: string;
  type: AssignmentType;
  link: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
};

export type AssignmentClassItem = {
  id: number;
  classId: string;
  name: string;
  description: string;
  createdAt: string;
  updatedAt: string;
};

export type AssignmentStudentItem = {
  id: number;
  classInfos: { classId: string; classGroup: { id: number } }[];
  name: string;
  uid: string;
  phone: string;
  email: string;
  birthday: string;
  createdAt: string;
};

export type VoucherBalanceItem = {
  type: AssignmentType;
  remaining: number;
};

/**
 * -----------------------------------------------------------------
 */

export type ClassGroupSummaryItem = {
  classGroupId: number;
  classGroupName: string;
  completionRate: number;
  studentCount: number;
  todoCount: number;
};

export enum AssignmentOrderByType {
  ASC = 'ASC',
  DESC = 'DESC',
  REMAIN_DAYS_ASC = 'REMAIN_DAYS_ASC',
  REMAIN_DAYS_DESC = 'REMAIN_DAYS_DESC',
  /** 최신순 (내준날짜 최신 먼저) */
  LATEST = 'LATEST',
  /** 미완료순 (미완료 과제 먼저, 같은 상태 내에서는 최신순) */
  INCOMPLETE_FIRST = 'INCOMPLETE_FIRST',
}

export enum AssignmentKindType {
  TEST = 'TEST',
  ASSIGNMENT = 'ASSIGNMENT',
}

export type TxInfo = {
  /** TX pk */
  id: number;
  /** book/chapter */
  book: string;
  /** lesson */
  lesson: string;
  /** code */
  code: string;
  /** title */
  title: string;
  /** 할일type */
  type: AssignmentType;
  /** 과제 종류 */
  todoKind: AssignmentKindType;
  /** 진행상태 */
  toDoStatus: AssignmentStatusType;
  /** 시작일 */
  fromDate: string;
  /** 종료일 */
  toDate: string;
  /** link */
  link: string;
  /** 반 평균 점수 (테스트 과제만, 제출자 없으면 null) */
  avgScore?: number | null;
  /** 생성일 */
  createdAt: string;
  /** 수정일 */
  updatedAt: string;
  /** 삭제일 */
  deletedAt: string | null;
};

export type AssignmentStatusByClassItem = {
  txId: string;
  txInfo: TxInfo;
};

export type AssignmentStatusByStudentItem = {
  /** pk */
  id: number;
  /** 학생이름 */
  name: string;
  /** 학생 UID */
  uid: string;
  /** 누적할일수 */
  accumulatedTodoCount: number;
  /** 누적뱃지수 */
  accumulatedBadgeCount: number;
  /** 조회 필터 기준 배정 과제 수 (학생별보기 카드의 "과제 N개") */
  todoCount: number;
  /** 완료 과제 수 (학생별보기 카드의 "N개 완료") */
  completedCount: number;
  /** 완료율 0~100 (학생별보기 카드의 진도바) */
  progressRate: number;
  /** 소속 반 ID (학생별보기 카드의 반 배지. 반 미지정이면 null) */
  classGroupId: number | null;
  /** 소속 반 이름 (반 미지정이면 null) */
  classGroupName: string | null;
  /** 보유 달란트 합계 */
  dalantTotal: number;
  /** 생성일 */
  createdAt: string;
  /** 수정일 */
  updatedAt: string;
  /** 삭제일 */
  deletedAt: string | null;
};

export type TestItem = {
  seq: number;
  correct: boolean;
  selectedAnswers: number[];
  correctAnswers: number[];
  explanation: string | null;
  points: number;
};

export type AssignmentStatusProgress = {
  /** 배정된 할일 pk */
  id: number;
  /** link */
  link: string;
  /** 진행률 */
  progress: number;
  /** 진행상태 */
  progressState: AssignmentStatusType;
  /** 시작일 */
  fromDate: string;
  /** 종료일 */
  toDate: string;
  /** 완료일 */
  completedDate: string;
  /** 테스트 점수 (0~100). 테스트 과제가 아니거나 미제출이면 null */
  testScore?: number | null;
  /** 정오표 - 문항별 채점 결과 (테스트 과제 제출 시에만, 그 외 null) */
  testItems?: TestItem[] | null;
  /** 인증제 합격 날짜 */
  passDate: string;
  /** 인증제 실패 날짜 */
  failDate: string;
  /** 인증제 테스트 날짜 */
  testDate: string;
  /** 할일type */
  type: AssignmentType;
  /** 젭스플레이어 link */
  playerLink: string;
  /** 녹음개수 */
  recordingCount: number;
  /** 학습 완료 횟수 */
  completedCount: number;
};

export type AssignmentStatusStudent = {
  /** 학생 pk */
  id: number;
  /** 유저 pk */
  user_id: string;
  /** 학생 이름 */
  name: string;
  /** 학생 uid */
  uid: string;
  /** 구젭스 id */
  legacyJebsId: string;
};

export type AssignmentStatusDetailByClassItem = {
  progress: AssignmentStatusProgress;
  student: AssignmentStatusStudent;
};

export type AssignmentStatusDetailByStudentItem = {
  /** pk */
  id: number;
  /** book/chapter */
  book: string;
  /** lesson */
  lesson: string;
  /** viewId */
  viewId: string;
  /** code */
  code: string;
  /** title */
  title: string;
  /** result(상세보기) */
  result: string;
  /** type */
  type: AssignmentType;
  /** 진행률 */
  progress: number;
  /** 진행상태 */
  progressState: AssignmentStatusType;
  /** 완료일 */
  completedDate: string;
  /** 시작일 */
  fromDate: string;
  /** 종료일 */
  toDate: string;
  /** 인증제 합격 날짜 */
  passDate: string;
  /** 인증제 실패 날짜 */
  failDate: string;
  /** 인증제 테스트 날짜 */
  testDate: string;
  /** 과제 종류 */
  todoKind: AssignmentKindType;
  testScore?: number | null;
  /** 정오표 - 문항별 채점 결과 (테스트 과제 제출 시에만, 그 외 null) */
  testItems?: TestItem[] | null;
  /** 젭스플레이어 link */
  playerLink: string;
  /** 녹음개수 */
  recordingCount: number;
  /** 학습 완료 횟수 */
  completedCount: number;
  /** 생성일 */
  createdAt: string;
  /** 수정일 */
  updatedAt: string;
  /** 삭제일 */
  deletedAt: string | null;
};

/**
 * -----------------------------------------------------------------
 */

export type AssignmentRecordingItem = {
  /** 녹음 ID */
  recordingId: number;
  /** 과제 ID */
  todoId: number;
  /** 학생 ID */
  studentId: number;
  /** 단계 코드 */
  stepCode: string;
  /** 문항 seq (스텝 내 콘텐츠 순번, content_seq 컬럼 도입 전 구버전 녹음은 null) */
  /** 문항 seq (스텝 내 콘텐츠 순번, 1부터 시작). 구버전 녹음은 null — FE는 null이면 파일명 마커 파싱으로 폴백 */
  contentSeq: number | null;
  /** 문항 이름 */
  contentName: string | null;
  /** S3 URL */
  s3Url: string;
  /** 평가 상태 'pending', 'approved', 'rejected' */
  status: string;
  /** 교사 코멘트 */
  evaluatorComment: string;
  /** 녹음 일시 */
  createdAt: string;
  /** 평가 일시 */
  evaluatedAt: string;
  /** STT 변환 텍스트 (transcribe 호출 후 저장됨, 미변환 시 null) */
  recognizedText?: string | null;
  /**
   * [Phase2] AI 발음 평가 결과 (교사가 어드민에서 [AI 평가] 실행 후 저장됨, 미평가 시 null)
   *
   * analyzeStatus로 상태 구분:
   * - SUCCESS: 정상 평가 완료 (점수 유효)
   * - RECOGNITION_FAILED: 음성 인식 실패 (무음/잡음, 점수 0)
   * - TIMEOUT: Azure 타임아웃 (점수 0)
   * - ERROR: 기타 에러 (점수 0)
   */
  pronunciation?: {
    analyzeStatus: 'SUCCESS' | 'RECOGNITION_FAILED' | 'TIMEOUT' | 'ERROR';
    accuracyScore: number;
    fluencyScore: number;
    completenessScore: number;
    prosodyScore: number;
    overallScore: number;
    recognizedText: string | null;
    referenceText: string | null;
  } | null;

  /** 진도 반영 실패 여부 (평가는 저장되었으나 진도 업데이트 실패 시 true) 진도 반영 실패 여부 (true일 경우 진도 미반영) */
  progressUpdateFailed?: boolean;
};

export type AssignmentRecordingDownloadUrl = {
  downloadUrl: string;
  fileName: string;
  expiresInSeconds: number;
};
